import React, { useEffect, useMemo, useRef } from "react";
import styled, { useTheme } from "styled-components";
import {
  FiAlertCircle, FiCheckCircle, FiChevronRight, FiLayers, FiShield, FiTag, FiTarget, FiTruck, FiXCircle,
} from "react-icons/fi";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Section, SectionHeader, Widget, WidgetGrid, tokens } from "../activity-logs/styles";
import { Sparkline } from "../activity-logs/components/primitives";
import { IOS } from "../activity-logs/labels";
import {
  Bento, BentoTile, CompositionWidget, StepNumber, StorageBar, TopHeadline, TopRow,
} from "../creditos/styles";
import { SupplierAvatar } from "../creditos/components/parts";
import { tokens as T, hairline } from "../../styles/designTokens";
import { MISS } from "../revisor/home/AnaliseDashboard";
import { displayName } from "../revisor/home/analiseData";
import { branchLabel, formatCount, formatPercent } from "../revisor/home/revisorData";
import XmlSupplierDirectory, { PARTIAL } from "./XmlSupplierDirectory";
import { buildSupplierStats } from "./xmlScanData";

/**
 * Dashboard da aba Fornecedores do XML Scan, na estrutura da mesma aba de Créditos.
 * Pergunta da tela: quanto da base já emite com os campos da Reforma, quem pesa e
 * quem vale acionar? Elemento próprio: o porte, que separa a cauda longa de
 * fornecedores com poucos XMLs dos poucos que concentram o volume.
 * Cada corte filtra a lista completa, no fim da tela.
 */

const READY_COLORS = { full: T.brand, partial: PARTIAL, none: MISS };
const READY_ICONS = { full: <FiCheckCircle />, partial: <FiAlertCircle />, none: <FiXCircle /> };
const REGIME_ICONS = { none: <FiXCircle />, integral: <FiTag />, mixed: <FiLayers />, immune: <FiShield />, other: <FiTag /> };
const REGIME_COLORS = { none: MISS, integral: T.brand, mixed: "#6b9cf0", immune: "#a99af0", other: "#f2c27a" };

// ─── Maiores fornecedores: comprimento proporcional ao total de XMLs ─────────

const BarList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px -8px 0;

  button {
    display: grid;
    grid-template-columns: minmax(0, 210px) minmax(0, 1fr) 62px 14px;
    align-items: center;
    column-gap: 14px;
    width: calc(100% - 0px);
    padding: 7px 8px;
    border: none;
    border-radius: 9px;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background-color ${T.durBase} ${T.easeOut};
  }
  button:hover { background: rgba(118,118,128,0.08); }
  button:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .who { min-width: 0; }
  .nm { font-size: 0.84em; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sb { font-size: 0.72em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .track { display: flex; gap: 2px; height: 10px; }
  .track span { height: 100%; border-radius: 2px; transition: width 700ms ${T.easeOut}; }
  .v { text-align: right; font-size: 0.84em; font-weight: 600; font-variant-numeric: tabular-nums; }
  svg { font-size: 14px; opacity: 0; color: ${({ theme }) => tokens.label3(theme)}; transition: opacity ${T.durBase} ${T.easeOut}; }
  button:hover svg, button:focus-visible svg { opacity: 1; }
  @media (max-width: 720px) {
    button { grid-template-columns: minmax(0, 1fr) 54px; row-gap: 5px; }
    .track { grid-column: 1 / -1; grid-row: 2; }
    svg { display: none; }
  }
`;

function TopBars({ suppliers, onOpen }) {
  const max = Math.max(1, suppliers[0]?.total || 0);
  return (
    <BarList>
      {suppliers.map((s) => (
        <button key={s.key} type="button" onClick={() => onOpen(s.raw)} title={`${s.name} · ${s.cnpj}`}>
          <span className="who">
            <div className="nm">{displayName(s.name)}</div>
            <div className="sb">{branchLabel(s.cnpj)} · {formatPercent(s.pct)} conformes</div>
          </span>
          <span className="track" role="img" aria-label={`${formatCount(s.conformes)} conformes, ${formatCount(s.parciais)} parciais, ${formatCount(s.naoConformes)} não conformes`}>
            {s.conformes > 0 && <span style={{ width: `${(s.conformes / max) * 100}%`, background: T.brand, opacity: 0.85 }} />}
            {s.parciais > 0 && <span style={{ width: `${(s.parciais / max) * 100}%`, background: PARTIAL, opacity: 0.9 }} />}
            {s.naoConformes > 0 && <span style={{ width: `${(s.naoConformes / max) * 100}%`, background: MISS }} />}
          </span>
          <span className="v">{formatCount(s.total)}</span>
          <FiChevronRight />
        </button>
      ))}
    </BarList>
  );
}

// ─── Porte ───────────────────────────────────────────────────────────────────

function SizeTip({ active, payload }) {
  const theme = useTheme();
  const b = active && payload?.[0]?.payload;
  if (!b) return null;
  const rows = [["Todos conformes", b.full, T.brand], ["Em adaptação", b.partial, PARTIAL], ["Nenhum conforme", b.none, MISS]];
  return (
    <div style={{
      background: tokens.solid(theme), border: `1px solid ${tokens.separator(theme)}`, borderRadius: 10, padding: "10px 12px", minWidth: 190,
      fontSize: 12, fontVariantNumeric: "tabular-nums", color: tokens.label(theme), boxShadow: tokens.dk(theme) ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.08)",
    }}
    >
      <div style={{ fontWeight: 600 }}>{b.label}</div>
      <div style={{ marginBottom: 6, color: tokens.label2(theme) }}>{formatCount(b.count)} fornecedores · {formatCount(b.xmls)} XMLs</div>
      {rows.map(([label, v, c]) => (
        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 2 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{label}</span>
          <b style={{ fontWeight: 600 }}>{formatCount(v)}</b>
        </div>
      ))}
    </div>
  );
}

function SizeTick({ x, y, payload, data }) {
  const theme = useTheme();
  const b = data[payload.index];
  if (!b) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" dy={10} fontSize={10} fill={tokens.label(theme)}>{b.short}</text>
      <text textAnchor="middle" dy={23} fontSize={9.5} fill={tokens.label2(theme)}>{formatPercent(b.xmlShare)}{window.innerWidth <= 900 ? "" : " XMLs"}</text>
    </g>
  );
}

function SizeChart({ sizes, onSelect }) {
  const theme = useTheme();
  const axis = { fontSize: 10, fill: tokens.label2(theme) };
  const pick = (entry) => { const b = entry?.payload || entry; if (b?.key) onSelect(b); };
  return (
    <div style={{ height: 210, marginTop: 10 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={sizes} margin={{ top: 6, right: 0, left: 0, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />
          <XAxis dataKey="short" tick={<SizeTick data={sizes} />} height={34} axisLine={false} tickLine={false} interval={0} />
          <YAxis tick={axis} axisLine={false} tickLine={false} width={32} tickFormatter={(v) => formatCount(v)} />
          <Tooltip content={<SizeTip />} cursor={{ fill: "rgba(118,118,128,0.08)", radius: 6 }} />
          <Bar dataKey="full" stackId="s" fill={T.brand} fillOpacity={0.8} maxBarSize={44} isAnimationActive={false} cursor="pointer" onClick={pick} />
          <Bar dataKey="partial" stackId="s" fill={PARTIAL} fillOpacity={0.85} maxBarSize={44} isAnimationActive={false} cursor="pointer" onClick={pick} />
          <Bar dataKey="none" stackId="s" fill={MISS} fillOpacity={0.9} maxBarSize={44} radius={[4, 4, 0, 0]} isAnimationActive={false} cursor="pointer" onClick={pick} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Estados ─────────────────────────────────────────────────────────────────

const StateRows = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px -6px 0;

  button {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr) 90px 62px;
    align-items: center;
    column-gap: 10px;
    padding: 7px 6px;
    border: none;
    border-radius: 9px;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    font-variant-numeric: tabular-nums;
    transition: background-color ${T.durBase} ${T.easeOut};
  }
  button + button { box-shadow: inset 0 0.5px 0 ${({ theme }) => hairline(theme, true)}; }
  button:hover { background: rgba(118,118,128,0.08); }
  button:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .uf { font-size: 0.76em; font-weight: 600; text-align: center; padding: 3px 0; border-radius: 6px; background: rgba(118,118,128,0.12); }
  .nm { min-width: 0; font-size: 0.84em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .nm small { margin-left: 6px; font-size: 0.86em; color: ${({ theme }) => tokens.label2(theme)}; }
  .bar { height: 4px; border-radius: 2px; background: rgba(118,118,128,0.14); overflow: hidden; }
  .bar span { display: block; height: 100%; border-radius: 2px; background: ${T.brand}; }
  .p { text-align: right; font-size: 0.8em; font-weight: 600; }
`;

// ─── Tela ────────────────────────────────────────────────────────────────────

export default function XmlSupplierDashboard({ suppliers, filter, onFilter, extra, onExtra, onOpen }) {
  const theme = useTheme();
  const stats = useMemo(() => buildSupplierStats(suppliers), [suppliers]);
  const directory = useRef(null);
  const toDirectory = () => directory.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const choose = (label, test) => { onFilter(null); onExtra({ label, test }); toDirectory(); };

  // Quem chega por um filtro de outra aba vê a lista, não o topo do dashboard
  useEffect(() => {
    if (filter || extra) directory.current?.scrollIntoView({ block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const none = stats.ready.find((r) => r.key === "none");
  const full = stats.ready.find((r) => r.key === "full");
  const [firstRegime, secondRegime, ...restRegime] = stats.regimes;

  const RegimeTile = ({ row, size }) => (
    <BentoTile type="button" $color={REGIME_COLORS[row.key]} $size={size} title={`${row.label}: ${formatCount(row.count)} fornecedores, ${formatCount(row.xmls)} XMLs`} onClick={() => choose(row.label, row.test)}>
      <span className="top">
        <span className="pct">{formatPercent(row.share).replace("%", "")}<small>%</small></span>
        <span className="icon">{REGIME_ICONS[row.key]}</span>
      </span>
      <span className="bottom">
        <span className="label">{row.label}</span>
        <span className="amount">{size === "small" ? formatCount(row.count) : `${formatCount(row.count)} fornecedores`}</span>
        <span className="track"><span className="level" style={{ width: `${row.share}%`, background: REGIME_COLORS[row.key] }} /></span>
      </span>
    </BentoTile>
  );

  return (
    <>
      {/* KPIs */}
      <Section $first>
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head"><FiTruck style={{ color: IOS.blue, opacity: 1 }} /> Fornecedores</div>
            <div className="w-value">{formatCount(stats.count)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{stats.groups < stats.count ? `${formatCount(stats.groups)} empresas distintas` : "nesta análise"}</span>
              <Sparkline data={stats.sizes.map((b) => b.count)} color={IOS.blue} width={80} height={24} points={60} />
            </div>
          </Widget>
          <Widget $delay={50} $clickable onClick={() => choose("Todos conformes", full.test)}>
            <div className="w-head"><FiCheckCircle style={{ color: T.brand, opacity: 1 }} /> Todos os XMLs conformes</div>
            <div className="w-value">{formatCount(full.count)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatPercent(full.share)} dos fornecedores</span>
              <Sparkline data={stats.sizes.map((b) => b.full)} color={T.brand} width={80} height={24} points={60} />
            </div>
          </Widget>
          <Widget $delay={100} $clickable onClick={() => choose("Nenhum conforme", none.test)}>
            <div className="w-head"><FiXCircle style={{ color: MISS, opacity: 1 }} /> Nenhum XML conforme</div>
            <div className="w-value">{formatCount(none.count)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>com {formatPercent(none.xmlShare)} dos XMLs</span>
              <Sparkline data={stats.sizes.map((b) => b.none)} color={MISS} width={80} height={24} points={60} />
            </div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head"><FiTarget style={{ color: IOS.indigo, opacity: 1 }} /> Concentram 80% dos XMLs</div>
            <div className="w-value">{formatCount(stats.forEighty)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatPercent(stats.eightyShare)} dos fornecedores</span>
              <Sparkline data={stats.concentration} color={IOS.indigo} width={80} height={24} points={60} />
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      {/* Maiores + prontidão */}
      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={200}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div className="w-title">Maiores fornecedores</div>
                <div className="w-sub">Os {formatCount(stats.top.length)} primeiros somam {formatPercent(stats.topShare)} dos XMLs. O comprimento da barra é o total de XMLs de cada um</div>
              </div>
              <div className="w-eyebrow" style={{ display: "flex", gap: 14 }}>
                {[["Conformes", T.brand], ["Parciais", PARTIAL], ["Não conformes", MISS]].map(([label, c]) => (
                  <span key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />{label}</span>
                ))}
              </div>
            </div>
            <TopBars suppliers={stats.top} onOpen={onOpen} />
          </Widget>

          <CompositionWidget $delay={250} style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-title">Prontidão</div>
            <div className="w-sub">Clique para filtrar a lista</div>
            <StorageBar role="img" aria-label="Fornecedores por prontidão" style={{ marginTop: 14 }}>
              {stats.ready.filter((r) => r.count > 0).map((r) => <span key={r.key} title={`${r.label}: ${formatCount(r.count)}`} style={{ flexGrow: r.count, background: READY_COLORS[r.key] }} />)}
            </StorageBar>
            <div style={{ marginTop: 8 }}>
              {stats.ready.map((r) => (
                <TopRow key={r.key} type="button" $compact onClick={() => choose(r.label, r.test)} disabled={r.count === 0}>
                  <span className="lead"><StepNumber $color={READY_COLORS[r.key]}>{READY_ICONS[r.key]}</StepNumber></span>
                  <span className="main">
                    <span className="title">{r.label}</span>
                    <span className="sub">{formatCount(r.xmls)} XMLs</span>
                  </span>
                  <span className="end">
                    <span className="value">{formatCount(r.count)}</span>
                    <span className="share">{formatPercent(r.share)}</span>
                  </span>
                </TopRow>
              ))}
            </div>
            <div style={{ marginTop: "auto", paddingTop: 12 }}>
              {[
                ["Mediana por fornecedor", `${stats.medianXmls.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} XMLs`],
                ["Média por fornecedor", `${stats.averageXmls.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} XMLs`],
                ["Com um único XML", formatCount(stats.singleXml)],
              ].map(([label, value]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", fontSize: "0.8em", borderTop: `0.5px solid ${hairline(theme, true)}` }}>
                  <span style={{ color: tokens.label2(theme) }}>{label}</span>
                  <b style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</b>
                </div>
              ))}
            </div>
          </CompositionWidget>
        </WidgetGrid>
      </Section>

      {/* Porte + regime */}
      <Section $delay={140}>
        <WidgetGrid>
          <Widget $span={2} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-title">Fornecedores por porte</div>
            <div className="w-sub">
              {stats.tail.count > 0
                ? `${formatCount(stats.tail.none)} dos ${formatCount(none.count)} sem nenhum XML conforme têm até 5 XMLs. Essa faixa inteira soma ${formatPercent(stats.tail.xmlShare)} do volume. Clique para filtrar`
                : "Quantos fornecedores há em cada faixa de XMLs. Clique para filtrar"}
            </div>
            <SizeChart sizes={stats.sizes} onSelect={(b) => choose(b.label, b.test)} />
          </Widget>

          <Widget $span={2} $delay={350} style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-title">O que declaram</div>
            <div className="w-sub">Situação tributária (CST) que cada fornecedor usa nos XMLs. Clique para filtrar</div>
            {firstRegime && (
              <Bento>
                <RegimeTile row={firstRegime} size="large" />
                {secondRegime && (
                  <div className="side">
                    <RegimeTile row={secondRegime} size="medium" />
                    {restRegime.length > 0 && <div className="base">{restRegime.map((r) => <RegimeTile key={r.key} row={r} size="small" />)}</div>}
                  </div>
                )}
              </Bento>
            )}
          </Widget>
        </WidgetGrid>
      </Section>

      {/* Quem acionar + estados */}
      <Section $delay={200}>
        <WidgetGrid $cols={2}>
          <Widget $delay={400}>
            <div className="w-title">Quem acionar primeiro</div>
            <div className="w-sub">Sem nenhum XML conforme, do maior volume ao menor</div>
            {stats.priority.length === 0 ? <div className="w-sub" style={{ marginTop: 16 }}>Nenhum fornecedor nesta situação.</div> : (
              <>
                <TopHeadline>
                  <span className="n">{formatPercent(stats.priorityShare).replace("%", "")}<span className="pct">%</span></span>
                  <span className="l">dos XMLs desse grupo vêm de {formatCount(stats.priority.length)} fornecedores</span>
                </TopHeadline>
                <div style={{ marginTop: 6 }}>
                  {stats.priority.map((s) => (
                    <TopRow key={s.key} type="button" onClick={() => onOpen(s.raw)}>
                      <span className="lead"><SupplierAvatar name={s.name} size={32} /></span>
                      <span className="main">
                        <span className="title" title={s.name}>{displayName(s.name)}</span>
                        <span className="sub" title={s.cnpj}>{s.ufName || "UF não identificada"} · {branchLabel(s.cnpj)}</span>
                      </span>
                      <span className="end">
                        <span className="value">{formatCount(s.total)}</span>
                        <span className="share">XMLs</span>
                      </span>
                      <FiChevronRight className="chevron" />
                    </TopRow>
                  ))}
                </div>
              </>
            )}
          </Widget>

          <Widget $delay={450}>
            <div className="w-title">Por estado</div>
            <div className="w-sub">
              {formatCount(stats.states.length)} {stats.states.length === 1 ? "estado" : "estados"}, pelo emitente na chave de acesso. A barra é a parte dos XMLs conforme. Clique para filtrar
            </div>
            <StateRows>
              {stats.states.slice(0, 7).map((u) => (
                <button key={u.code} type="button" title={`${u.name}: ${formatCount(u.count)} fornecedores, ${formatCount(u.xmls)} XMLs`} onClick={() => choose(u.name, u.test)}>
                  <span className="uf">{u.sigla}</span>
                  <span className="nm">{formatCount(u.count)} fornecedores<small>{formatCount(u.xmls)} XMLs</small></span>
                  <span className="bar"><span style={{ width: `${u.pct}%` }} /></span>
                  <span className="p">{formatPercent(u.pct)}</span>
                </button>
              ))}
            </StateRows>
            {stats.states.length > 7 && <div className="w-foot" style={{ marginTop: 8, color: tokens.label2(theme) }}>E mais {formatCount(stats.states.length - 7)} estados com {formatCount(stats.states.slice(7).reduce((s, u) => s + u.count, 0))} fornecedores.</div>}
          </Widget>
        </WidgetGrid>
      </Section>

      {/* Lista completa */}
      <Section $delay={240} ref={directory} style={{ scrollMarginTop: 180 }}>
        <SectionHeader><h2>Todos os fornecedores</h2></SectionHeader>
        <XmlSupplierDirectory
          suppliers={suppliers}
          filter={filter}
          onFilter={onFilter}
          extra={extra}
          onClearExtra={() => onExtra(null)}
          initialSort={filter === "parciais" ? "parciais" : "missing"}
          onOpen={onOpen}
        />
      </Section>
    </>
  );
}
